import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Ban, Check, CheckCircle2, Hourglass, Search, SkipForward, Timer } from "lucide-react";
import { DRAFT_ORDER, boardOf, stepOf, usedBy } from "@/lib/draft";
import { DDRAGON, ROLES } from "@/lib/drafterData";

export const SIDE_STYLE = {
  blue: { text: "text-sky-400", border: "border-sky-500", ring: "ring-sky-500", label: "bleu" },
  red: { text: "text-red-500", border: "border-red-500", ring: "ring-red-500", label: "rouge" },
};
export const SECONDARY_BTN = "flex items-center gap-1.5 rounded-xl border border-white/10 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:border-orange-500 disabled:opacity-40";
const PRIMARY_BTN = "flex items-center justify-center gap-1.5 rounded-xl bg-orange-500 px-5 py-2 font-heading text-base font-bold uppercase tracking-wide text-[#060B18] transition-all hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-40";
const iconUrl = (version, champ) => `${DDRAGON}/cdn/${version}/img/champion/${champ.image}`;

function PickSlot({ champ, preview, side, active, swappable, swapSelected, onClick }) {
  const shown = champ || preview;
  const highlight = active || swapSelected;
  const cls = `relative block h-10 w-full overflow-hidden rounded-lg border sm:h-20 sm:rounded-xl bg-white/[0.03] text-left sm:h-20 lg:h-24 ${
    highlight ? `${SIDE_STYLE[side].border} ring-2 ${SIDE_STYLE[side].ring}` : "border-white/10"
  } ${active ? "animate-pulse" : ""} ${swappable ? "cursor-pointer transition-colors hover:border-orange-500" : ""}`;
  const content = shown && (
    <>
      <img src={`${DDRAGON}/cdn/img/champion/centered/${shown.id}_0.jpg`} alt=""
        className={`absolute inset-0 h-full w-full object-cover object-[center_20%] ${champ ? "" : "opacity-50"}`} />
      <span className="absolute bottom-0.5 left-2 right-1 truncate font-heading text-[11px] font-bold uppercase drop-shadow sm:text-sm">{shown.name}</span>
    </>
  );
  return swappable ? (
    <button type="button" onClick={onClick} aria-pressed={swapSelected} aria-label={`Échanger ${champ.name}`} className={cls}>{content}</button>
  ) : (
    <div className={cls}>{content}</div>
  );
}

function BanSlot({ champ, preview, skipped, version, side, active }) {
  const shown = champ || preview;
  return (
    <div className={`flex aspect-square min-w-0 flex-1 items-center justify-center overflow-hidden rounded-md border bg-white/[0.03] lg:rounded-lg ${
      active ? `${SIDE_STYLE[side].border} ring-2 ${SIDE_STYLE[side].ring}` : "border-white/10"}`}>
      {shown && <img src={iconUrl(version, shown)} alt={shown.name} title={shown.name} className={`h-full w-full grayscale ${champ ? "" : "opacity-50"}`} />}
      {skipped && <Ban className="h-3.5 w-3.5 text-slate-500" aria-label="Ban passé" />}
    </div>
  );
}

function TeamColumn({ side, name, board, byId, version, step, preview, swappable, swapSel, onSwap }) {
  const { ban: bans, pick: picks } = board[side];
  const activeType = step?.side === side ? step.type : null;
  return (
    <div className={`flex min-w-0 flex-col gap-2 lg:gap-3 ${side === "red" ? "lg:order-3" : "lg:order-1"}`}>
      <p className={`truncate font-heading text-base font-extrabold uppercase tracking-tight sm:text-lg ${SIDE_STYLE[side].text}`}>{name}</p>
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => {
          const active = activeType === "ban" && i === bans.length;
          return <BanSlot key={i} champ={byId[bans[i]]} preview={active ? preview : null} skipped={i < bans.length && !bans[i]}
            version={version} side={side} active={active} />;
        })}
      </div>
      {[0, 1, 2, 3, 4].map((i) => {
        const active = activeType === "pick" && i === picks.length;
        return (
          <PickSlot key={i} champ={byId[picks[i]]} preview={active ? preview : null} side={side} active={active}
            swappable={swappable && !!byId[picks[i]]} swapSelected={swapSel?.side === side && swapSel.index === i} onClick={() => onSwap(side, i)} />
        );
      })}
    </div>
  );
}

// Plateau commun aux drafts locales et en ligne. `controls` liste les sides que cet appareil peut jouer.
export default function DraftBoard({ d, controls, champions, version, roles, clockOffset = 0, onAction, toolbar }) {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState(null);
  const [swapSel, setSwapSel] = useState(null);
  const [swapSides, setSwapSides] = useState(false);
  const [now, setNow] = useState(() => Date.now() / 1000 + clockOffset);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() / 1000 + clockOffset), 250);
    return () => clearInterval(t);
  }, [clockOffset]);

  const byId = useMemo(() => Object.fromEntries(champions.map((c) => [c.id, c])), [champions]);
  const board = boardOf(d.choices);
  const used = usedBy(d);
  const step = stepOf(d);
  const done = !step;
  const gameNumber = d.previous_games.length + 1;
  const seriesOver = done && gameNumber >= d.games;
  const canAct = d.started && step && controls.includes(step.side);
  const selected = step ? d.selected[step.side] : null;
  const left = d.deadline ? Math.max(0, Math.ceil(d.deadline - now)) : null;
  const hasRoles = Object.keys(roles).length > 0;

  const q = query.trim().toLowerCase();
  const visible = champions.filter((c) => (!q || c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
    && (!role || roles[c.id]?.includes(role)));

  const act = (action, side = step?.side) => onAction(side, action);
  const pick = (id) => { act({ type: "select", champion: id }); setQuery(""); };
  const onSwap = (side, index) => {
    if (!swapSel || swapSel.side !== side) return setSwapSel({ side, index });
    if (swapSel.index !== index) act({ type: "swap", a: swapSel.index, b: index }, side);
    setSwapSel(null);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm text-slate-400" data-testid="drafter-series-status">
          {d.mode === "fearless" ? "Fearless" : "Tournoi"} · Game {gameNumber}/{d.games}
          {d.mode === "fearless" && used.size > d.choices.filter(Boolean).length && ` · ${used.size - d.choices.filter(Boolean).length} champions verrouillés`}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">{toolbar}</div>
      </div>

      <div className="card-tactical sticky top-20 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 bg-[#0A1024]/95 p-3 backdrop-blur sm:p-4" aria-live="polite" data-testid="drafter-banner">
        {!d.started && !done ? (
          <>
            <div className="flex flex-wrap gap-2">
              {["blue", "red"].map((side) => (
                <span key={side} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${d.ready[side] ? "border-green-500/50 text-green-400" : "border-white/10 text-slate-400"}`}>
                  {d.ready[side] ? <CheckCircle2 className="h-4 w-4" /> : <Hourglass className="h-4 w-4" />}
                  <span className={SIDE_STYLE[side].text}>{d.names[side]}</span> {d.ready[side] ? "prêt" : "en attente"}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {controls.filter((side) => !d.ready[side]).map((side) => (
                <button key={side} onClick={() => act({ type: "ready" }, side)} data-testid={`drafter-ready-${side}`} className={PRIMARY_BTN}>
                  <Check className="h-4 w-4" /> {controls.length > 1 ? `${d.names[side]} prêt` : "Je suis prêt"}
                </button>
              ))}
              {!controls.length && <span className="text-sm text-slate-400">Mode spectateur : la draft démarre quand les deux capitaines sont prêts.</span>}
            </div>
          </>
        ) : done ? (
          <>
            <div>
              <p className="font-heading text-lg font-bold uppercase">{seriesOver ? "Série terminée" : "Draft terminée"}</p>
              {controls.length > 0 && (
                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400">
                  <ArrowLeftRight className="h-4 w-4 shrink-0 text-orange-500" /> Phase d'échange : cliquez deux champions de votre équipe pour les intervertir.
                </p>
              )}
            </div>
            {controls.length > 0 && !seriesOver && (
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={swapSides} onChange={(e) => setSwapSides(e.target.checked)} className="accent-orange-500" data-testid="drafter-swap-sides" />
                  Échanger les sides
                </label>
                <button onClick={() => act({ type: "next_game", swap_sides: swapSides }, controls[0])} data-testid="drafter-next-game" className={PRIMARY_BTN}>
                  Game suivante
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="font-heading text-base font-bold uppercase sm:text-lg">
              <span className={SIDE_STYLE[step.side].text}>{d.names[step.side]}</span>{" "}
              {step.type === "ban" ? `ban ${board[step.side].ban.length + 1}` : `pick ${board[step.side].pick.length + 1}`}
              <span className="ml-2 text-sm font-normal normal-case text-slate-500">({d.choices.length + 1}/{DRAFT_ORDER.length})</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {left !== null && (
                <span data-testid="drafter-timer" className={`flex items-center gap-1.5 font-mono2 text-lg font-bold tabular-nums ${left <= 10 ? "text-red-500" : "text-slate-200"}`}>
                  <Timer className="h-4 w-4" /> {left}s
                </span>
              )}
              {canAct ? (
                <>
                  {step.type === "ban" && (
                    <button onClick={() => act({ type: "skip" })} data-testid="drafter-skip-ban" className={SECONDARY_BTN}>
                      <SkipForward className="h-4 w-4" /> Passer
                    </button>
                  )}
                  <button onClick={() => act({ type: "lock" })} disabled={!selected} data-testid="drafter-validate" className={PRIMARY_BTN}>
                    <Check className="h-4 w-4" /> {step.type === "ban" ? "Bannir" : "Valider"}{selected && byId[selected] ? ` ${byId[selected].name}` : ""}
                  </button>
                </>
              ) : (
                <span className="text-sm text-slate-400">{controls.length ? "Tour adverse…" : "En attente du capitaine…"}</span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="mt-4 grid gap-4 lg:mt-6 lg:grid-cols-[14rem_1fr_14rem] lg:gap-6">
        <div className="grid grid-cols-2 gap-3 lg:contents">
          {["blue", "red"].map((side) => (
            <TeamColumn key={side} side={side} name={d.names[side]} board={board} byId={byId} version={version} step={step}
              preview={step?.side === side ? byId[d.selected[side]] : null}
              swappable={done && controls.includes(side)} swapSel={swapSel} onSwap={onSwap} />
          ))}
        </div>

        <div className="lg:order-2">
          <div className="flex flex-wrap gap-2">
            <label className="flex min-w-[12rem] flex-1 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 focus-within:border-orange-500">
              <Search className="h-4 w-4 text-slate-500" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un champion" aria-label="Rechercher un champion"
                data-testid="drafter-search" className="w-full bg-transparent text-sm outline-none placeholder:text-slate-500" />
            </label>
            {hasRoles && (
              <div className="flex gap-1 overflow-x-auto rounded-xl border border-white/10 p-1" role="group" aria-label="Filtrer par rôle">
                {[{ key: null, label: "Tous" }, ...ROLES].map((r) => (
                  <button key={r.label} onClick={() => setRole(r.key)} aria-pressed={role === r.key} data-testid={`drafter-role-${r.key || "all"}`}
                    className={`whitespace-nowrap rounded-lg px-2.5 py-1 text-xs font-bold uppercase transition-colors ${role === r.key ? "bg-orange-500 text-[#060B18]" : "text-slate-400 hover:text-white"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="mt-3 grid max-h-[55vh] grid-cols-[repeat(auto-fill,minmax(3.75rem,1fr))] gap-2 overflow-y-auto pr-1 sm:grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] lg:max-h-[36rem]" data-lenis-prevent>
            {visible.map((c) => {
              const isUsed = used.has(c.id);
              const isSelected = selected === c.id;
              return (
                <button key={c.id} onClick={() => pick(c.id)} onDoubleClick={() => !isUsed && act({ type: "lock" })}
                  disabled={isUsed || !canAct} title={c.name} aria-pressed={isSelected} data-testid={`drafter-champ-${c.id}`}
                  className="group flex flex-col items-center gap-1 disabled:cursor-not-allowed">
                  <img src={iconUrl(version, c)} alt="" loading="lazy"
                    className={`aspect-square w-full rounded-lg border transition-all ${
                      isUsed ? "border-white/10 opacity-25 grayscale" : isSelected ? "scale-105 border-orange-500 ring-2 ring-orange-500" : "border-white/10 group-enabled:group-hover:scale-105 group-enabled:group-hover:border-orange-500"
                    }`} />
                  <span className={`w-full truncate text-center text-[11px] ${isUsed ? "text-slate-600" : isSelected ? "text-orange-400" : "text-slate-300"}`}>{c.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
